import { GoalPeriod } from '@akin-home/amplify';
import { Goal } from '../types';

import {
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  getDaysInMonth,
  getWeeksInMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import {
  createGoalKey,
  getGoalCompletionProgress,
  getGoalCompletionRateSample,
  getGoalFrequencyDisplayText,
  getGoalProgressPercentage,
  incrementGoalCompletion,
} from '.';
import { getGoalMonthGridData } from './getGoalMonthGridData';

export const createGoalModel = (input?: Partial<Goal>): Goal => ({
  id: '',
  title: '',
  emoji: '🎯',
  period: GoalPeriod.day,
  completions: {},
  participant_ids: [],
  completions_per_period: 1,
  start: new Date(),
  category_ids: [],
  ...input,
  getFrequencyDisplay,
  incrementCompletion,
  getProgressPercentage,
  getCompletionRate,
  getCompletionsForWeek,
  toCompleteForWeek,
  getCompletionsForMonth,
  toCompleteForMonth,
  getCompletionRateSample: getGoalCompletionRateSample,
  getGoalMonthGridData,
});

export const createGoalTemplateModel = (input?: Partial<Goal>) =>
  createGoalModel({
    ...input,
    isTemplate: true,
    readGroups: ['common'],
    participant_ids: undefined, // Don't allow participants to be set
  });


function getFrequencyDisplay(this: Goal) {
  return getGoalFrequencyDisplayText(this);
};

function incrementCompletion(
  this: Goal,
  key: { user_id: string; date: Date },
  value = 1
) {
  return incrementGoalCompletion(this, key, value);
}

function getProgressPercentage(
  this: Goal,
  key: { user_id: string; date: Date }
) {
  return getGoalProgressPercentage(this, key);
}

// Gets the overall completion rate for a goal up to a given date
function getCompletionRate(
  this: Goal,
  targetDate: Date,
  user_id: string
): number {
  switch (this.period) {
    case GoalPeriod.day:
      return getDailyCompletionRate(this, targetDate, user_id);
    case GoalPeriod.week:
      return getWeeklyCompletionRate(this, targetDate, user_id);
    case GoalPeriod.month:
      return getMonthlyCompletionRate(this, targetDate, user_id);
    default:
      return 0.0;
  }
}

function getCompletionsForMonth(
  this: Goal,
  date: Date,
  user_id: string
): number {
  const start = startOfMonth(date);
  const end = endOfMonth(date);

  switch (this.period) {
    case GoalPeriod.day: {
      const days = eachDayOfInterval({
        start,
        end,
      });

      return days.reduce(
        (acc, day) =>
          acc + getGoalCompletionProgress(this, { date: day, user_id }),
        0
      );
    }
    case GoalPeriod.week: {
      const weeks = eachWeekOfInterval({
        start,
        end,
      });

      return weeks.reduce(
        (acc, day) =>
          acc + getGoalCompletionProgress(this, { date: day, user_id }),
        0
      );
    }
    case GoalPeriod.month:
      return getGoalCompletionProgress(this, {
        date,
        user_id,
      });

    default:
      return 0;
  }
}

function toCompleteForMonth(this: Goal, date: Date): number {
  switch (this.period) {
    case GoalPeriod.day:
      return this.completions_per_period * getDaysInMonth(date);
    case GoalPeriod.week:
      return this.completions_per_period * getWeeksInMonth(date);
    case GoalPeriod.month:
      return this.completions_per_period;
    default:
      return 0;
  }
}
function getCompletionsForWeek(
  this: Goal,
  date: Date,
  user_id: string
): number {
  const start = startOfWeek(date);
  const end = endOfWeek(date);

  if (this.period === GoalPeriod.day) {
    const days = eachDayOfInterval({
      start,
      end,
    });

    return days.reduce(
      (acc, day) =>
        acc + getGoalCompletionProgress(this, { date: day, user_id }),
      0
    );
  }

  return getGoalCompletionProgress(this, {
    date,
    user_id,
  });
}

function toCompleteForWeek(this: Goal): number {
  if (this.period === GoalPeriod.day) return this.completions_per_period * 7;
  return this.completions_per_period;
}

/** Gets how many days have had successful completion between Goal start date and given target date */
const getDailyCompletionRate = (
  goal: Goal,
  targetDate: Date,
  user_id: string
): number => {
  const { completions, completions_per_period } = goal;

  const startDate = goal.start || new Date();

  const days = eachDayOfInterval({
    start: startDate < targetDate ? startDate : targetDate,
    end: targetDate,
  });

  const completedDays = days.filter(
    (date) =>
      completions[createGoalKey({ date, period: GoalPeriod.day, user_id })]
        ?.progress >= completions_per_period
  );

  return completedDays.length / days.length;
};

/** Gets the weekly completion rate for a goal */
const getWeeklyCompletionRate = (
  goal: Goal,
  targetDate: Date,
  user_id: string
): number => {
  const { completions, completions_per_period } = goal;

  const start = goal.start || new Date();

  /** Get up to, but not including, the target week */
  const weeks = eachWeekOfInterval({
    start: start < targetDate ? start : targetDate,
    end: targetDate,
  });

  const completedWeeks = weeks.filter((date) => {
    return (
      completions[createGoalKey({ date, period: GoalPeriod.week, user_id })]
        ?.progress >= completions_per_period
    );
  });

  return completedWeeks.length / (weeks.length || 1);
};

/** Gets the monthly completion rate for a goal */
const getMonthlyCompletionRate = (
  goal: Goal,
  targetDate: Date,
  user_id: string
): number => {
  const { completions, completions_per_period } = goal;

  const startDate = goal.start || new Date();

  /** Get up to, but not including, the target month */
  const months = eachMonthOfInterval({
    start: startDate,
    end: targetDate,
  });

  const completedMonths = months.filter((date) => {
    return (
      completions[createGoalKey({ date, period: GoalPeriod.month, user_id })]
        ?.progress >= completions_per_period
    );
  });

  return completedMonths.length / (months.length || 1);
};
